import React, { useCallback } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSortAlt,
  faSortAmountAsc,
  faSortAmountDesc,
} from '@fortawesome/sharp-regular-svg-icons'
import { Box, Group, UnstyledButton, Center, Text } from '@mantine/core'
import { flexRender, Header } from '@tanstack/react-table'

import {
  ACTION_COLUMN_ID,
  DEFAULT_TABLE_PROPS,
  SELECT_COLUMN_ID,
  SORT_FILTER_TRAIT_SORT_DIRECTION_ASC,
  SORT_FILTER_TRAIT_SORT_DIRECTION_DESC,
} from '../../../constants'
import useHeaderFooterStyles from '../../../styles/useHeaderFooterStyles'
import { SortFilterTraitSort } from '../../../types/sort-filter-trait'
import type { HeaderColumnProps } from '../header'

export const getRelevantSortField = <T,>(header: Header<T, unknown>): string =>
  header.column.columnDef.meta?.alternateSortKey ?? header.id

const reverseRelevantSort = ({
  activeSort,
  relevantSort,
  setSort,
}: {
  relevantSort: SortFilterTraitSort
  setSort: (sort: SortFilterTraitSort[]) => void
  activeSort: SortFilterTraitSort[]
}): void => {
  const indexOfRelevantSort = activeSort.findIndex(
    (as) => as.field[0] === relevantSort.field[0],
  )

  if (indexOfRelevantSort > -1) {
    const newActiveSort = [...activeSort]
    newActiveSort[indexOfRelevantSort] = {
      ...relevantSort,
      direction:
        relevantSort.direction === SORT_FILTER_TRAIT_SORT_DIRECTION_ASC
          ? SORT_FILTER_TRAIT_SORT_DIRECTION_DESC
          : SORT_FILTER_TRAIT_SORT_DIRECTION_ASC,
    }
    setSort(newActiveSort)
    return
  }
}

const HeaderColumn = <T,>({
  header,
  setSort,
  activeSort,
  tableProps = DEFAULT_TABLE_PROPS,
}: HeaderColumnProps<T>): React.ReactElement => {
  const canSort = header.column.getCanSort()

  const relevantSortKey = getRelevantSortField<T>(header)

  const relevantSort = activeSort.find(
    (sort) => sort.field[0] === relevantSortKey,
  )
  const { cx, classes } = useHeaderFooterStyles({
    tableFontSize: tableProps.fontSize ?? DEFAULT_TABLE_PROPS.fontSize,
  })

  const onClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (event) => {
      if (header.column.columnDef.id === SELECT_COLUMN_ID) {
        event.preventDefault()
        return
      }

      if (!canSort) return

      if (event.shiftKey) {
        if (relevantSort) {
          if (activeSort.length > 1) {
            setSort(
              activeSort.filter((sort) => sort.field[0] !== relevantSortKey),
            )
          } else {
            reverseRelevantSort({ relevantSort, setSort, activeSort })
          }
        } else {
          setSort([
            ...activeSort,
            {
              field: [relevantSortKey],
              direction: SORT_FILTER_TRAIT_SORT_DIRECTION_DESC,
            },
          ])
        }
      } else {
        if (relevantSort) {
          reverseRelevantSort({ relevantSort, setSort, activeSort })
        } else {
          setSort([
            {
              field: [relevantSortKey],
              direction: SORT_FILTER_TRAIT_SORT_DIRECTION_DESC,
            },
          ])
        }
      }
    },
    [canSort, relevantSort, relevantSortKey, activeSort, setSort],
  )

  return (
    <th
      className={classes.th}
      colSpan={header.colSpan}
      style={{
        minWidth: header.column.columnDef.minSize ?? 'auto',
        width: header.column.getSize() ?? 'auto',
        maxWidth: header.column.columnDef.maxSize ?? 'auto',
      }}
    >
      {header.column.columnDef.id === ACTION_COLUMN_ID ? (
        <Box>
          {flexRender(header.column.columnDef.header, header.getContext())}
        </Box>
      ) : (
        <UnstyledButton
          onClick={onClick}
          className={cx(classes.control, classes.headerPadding, {
            [classes.hoverControl]: canSort,
          })}
        >
          <Group position="apart">
            <Text
              fw={500}
              fz={tableProps?.fontSize ?? DEFAULT_TABLE_PROPS.fontSize}
            >
              {flexRender(header.column.columnDef.header, header.getContext())}
            </Text>
            {canSort && (
              <Center>
                <FontAwesomeIcon
                  className={cx(classes.sortIcon, {
                    [classes.inactiveSortIcon]: !relevantSort,
                  })}
                  icon={
                    relevantSort
                      ? relevantSort.direction ===
                        SORT_FILTER_TRAIT_SORT_DIRECTION_DESC
                        ? faSortAmountDesc
                        : faSortAmountAsc
                      : faSortAlt
                  }
                />
              </Center>
            )}
          </Group>
        </UnstyledButton>
      )}
    </th>
  )
}

export default HeaderColumn
